import "./index.css";

import axios from "axios";
import React from "react";
import { Auth0Provider } from "@auth0/auth0-react";

// Use API base URL from env (for Docker/deployment); generated client falls back to BASE_PATH otherwise.
if (typeof process.env.REACT_APP_API_URL === "string" && process.env.REACT_APP_API_URL) {
	axios.defaults.baseURL = process.env.REACT_APP_API_URL;
}
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { toast } from "react-toastify";

import App from "./App";
import { Auth0AxiosBridge } from "./Auth/Auth0AxiosBridge";
import { auth0Config, bypassAuth, getAuth0MissingFields } from "./Auth/auth0Config";
import { onRedirectCallback } from "./Auth/redirectCallback";
import { store } from "./Redux/Index";

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error?.response?.status === 401) {
            return Promise.reject(error);
        }
        // Panne réseau / requête annulée : pas de réponse du tout, un toast générique suffit.
        if (!error?.response) {
            console.error(error);
            toast.error("Le serveur est injoignable. Vérifiez votre connexion.");
            return Promise.reject(error);
        }
        // Le corps peut être une chaîne brute OU un objet JSON ({ message, title… }) : le
        // toString() naïf d'un objet donne « [object Object] », on extrait un vrai message.
        const data = error.response.data;
        let errorMessage: string =
            typeof data === "string"
                ? data
                : data?.message ?? data?.title ?? (data ? JSON.stringify(data) : "");

        console.error(errorMessage);
        errorMessage = errorMessage.replace("System.Exception: ", "");
        if (!errorMessage) errorMessage = `La requête a échoué (code ${error.response.status}).`;

        if (error.response.status == 429 || errorMessage.indexOf("Exceptions.ExceptionToManyRequest") >= 0) {
            toast.warn("To many request!");
        } else {
            toast.error(errorMessage.length > 200 ? errorMessage.substring(0, 199) + "..." : errorMessage);
        }

        return Promise.reject(error);
    }
);

const missingFields = getAuth0MissingFields();
const hasAuth0Config = missingFields.length === 0;
const authorizationParams = {
    redirect_uri: window.location.origin,
    ...(auth0Config.audience ? { audience: auth0Config.audience } : {}),
};
// Post-login landing (appState.returnTo) : routed through the ConnectedRouter history so deep
// links (e.g. /tickets?nouveau=1&…) survive the Auth0 round-trip. See Auth/redirectCallback.ts.

// Fetch the runtime config from the API before rendering so URL resolvers
// (storage previews, image picker, etc.) see the right base URLs for the
// current environment. We don't block React on auth : a network failure here
// just falls back to the build-time constants.
const loadRuntimeConfig = async () => {
    try {
        const res = await axios.get("/api/public-config");
        if (res?.data && typeof window !== "undefined") {
            window.__BCC_RUNTIME_CONFIG__ = res.data;
        }
    } catch (err) {
        console.warn("Runtime config fetch failed : falling back to build-time defaults.", err);
    }
};

const container = document.getElementById("root");
const renderApp = () => {
    if (!container) return;
    const root = createRoot(container);
    root.render(
        <React.StrictMode>
            {bypassAuth ? (
                <Auth0Provider
                    domain={auth0Config.domain || "bypass.local"}
                    clientId={auth0Config.clientId || "bypass"}
                    authorizationParams={authorizationParams}
                    skipRedirectCallback
                >
                    <Provider store={store}>
                        <App />
                    </Provider>
                </Auth0Provider>
            ) : hasAuth0Config ? (
                <Auth0Provider
                    domain={auth0Config.domain!}
                    clientId={auth0Config.clientId!}
                    authorizationParams={authorizationParams}
                    onRedirectCallback={onRedirectCallback}
                    cacheLocation="localstorage"
                    useRefreshTokens
                >
                    <Auth0AxiosBridge audience={auth0Config.audience} />
                    <Provider store={store}>
                        <App />
                    </Provider>
                </Auth0Provider>
            ) : (
                <div style={{ padding: 24 }}>
                    <h3>Missing Auth0 configuration</h3>
                    <ul>
                        {missingFields.map((field) => (
                            <li key={field}>{field}</li>
                        ))}
                    </ul>
                </div>
            )}
        </React.StrictMode>
    );
};

loadRuntimeConfig().finally(renderApp);
